import { useState } from "react";
import { useMutation } from "convex/react";
import { api } from "@constructionautomation/backend";
import { ArrowRight, Check, Circle, ListChecks, Loader2, X } from "lucide-react";

import { useProject } from "@/components/projects";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useBillingSession } from "@/lib/billing-session";
import { notifyError } from "@/lib/notify-error";
import { cn } from "@/lib/utils";
import { describeGuideAction, GUIDE_STEP_LABELS } from "./guide-content";
import type { ViewerGuide } from "./use-viewer-guide";

type Guide = NonNullable<ViewerGuide["guide"]>;

interface GettingStartedCardProps {
  guide: Guide;
  onCreateProject: () => void;
}

/**
 * First-use checklist with exactly one primary action. Progress comes from the user's real work,
 * so it resumes on any device; hiding it is remembered and never blocks the page.
 */
export function GettingStartedCard({ guide, onCreateProject }: GettingStartedCardProps) {
  const { switchProject } = useProject();
  const billing = useBillingSession();
  const dismiss = useMutation(api.onboarding.dismissGuide);
  const [isDismissing, setIsDismissing] = useState(false);

  const content = describeGuideAction(guide.nextAction, guide.focusProject?.name ?? null);
  const doneCount = guide.steps.filter((step) => step.state === "done").length;
  const command = content.command;

  const runCommand = () => {
    if (!command) return;
    if (command.kind === "create_project") onCreateProject();
    else if (command.kind === "set_up_billing") void billing.open();
    else switchProject(command.projectId, command.target);
  };

  const handleDismiss = async () => {
    setIsDismissing(true);
    try {
      await dismiss({});
    } catch (error) {
      notifyError(error, "Couldn't hide the guide. Try again.");
      setIsDismissing(false);
    }
  };

  return (
    <Card className="gap-4 py-4" aria-labelledby="getting-started-title">
      <CardHeader className="grid-cols-[1fr_auto] px-4 sm:px-6">
        <div className="min-w-0 space-y-1">
          <CardTitle id="getting-started-title" className="text-lg font-bold tracking-tight">
            Get started with SpecPilot
          </CardTitle>
          <CardDescription>
            Turn a schedule PDF into a submittal export. {doneCount} of {guide.steps.length} steps done.
          </CardDescription>
        </div>
        <IconButton
          label="Hide getting started guide"
          tooltip="Hide guide"
          icon={isDismissing ? Loader2 : X}
          iconClassName={isDismissing ? "animate-spin" : undefined}
          onClick={() => void handleDismiss()}
          disabled={isDismissing}
        />
      </CardHeader>
      <CardContent className="grid gap-4 px-4 sm:px-6 md:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)]">
        <ol className="space-y-2" aria-label="Setup steps">
          {guide.steps.map((step) => (
            <li
              key={step.key}
              aria-current={step.state === "current" ? "step" : undefined}
              className={cn(
                "flex items-center gap-2 text-sm",
                step.state === "current" ? "font-semibold text-foreground" : "text-muted-foreground",
              )}
            >
              {step.state === "done" ? (
                <Check className="size-4 shrink-0 text-status-completed" aria-hidden="true" />
              ) : step.state === "current" ? (
                <ArrowRight className="size-4 shrink-0 text-primary" aria-hidden="true" />
              ) : (
                <Circle className="size-4 shrink-0" aria-hidden="true" />
              )}
              <span className={cn(step.state === "done" && "line-through")}>{GUIDE_STEP_LABELS[step.key]}</span>
              {step.state === "done" && <span className="sr-only">(done)</span>}
            </li>
          ))}
        </ol>
        <section
          aria-labelledby="getting-started-next"
          aria-live="polite"
          className="flex flex-col gap-3 rounded-lg border-2 border-border bg-muted/40 p-4"
        >
          <div className="space-y-1">
            <h3 id="getting-started-next" className="text-base font-bold tracking-tight">
              {content.title}
            </h3>
            <p className="text-sm text-muted-foreground">{content.description}</p>
          </div>
          {content.actionLabel && command && (
            <Button
              className="w-full sm:w-auto sm:self-start"
              onClick={runCommand}
              disabled={command.kind === "set_up_billing" && billing.pendingIntent !== null}
            >
              {command.kind === "set_up_billing" && billing.pendingIntent !== null && (
                <Loader2 className="size-4 animate-spin" />
              )}
              {content.actionLabel}
            </Button>
          )}
        </section>
      </CardContent>
    </Card>
  );
}

/** Brings a hidden guide back; small enough not to compete with the page's own actions. */
export function ResumeGuideButton() {
  const resume = useMutation(api.onboarding.resumeGuide);
  const [isResuming, setIsResuming] = useState(false);
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => {
        setIsResuming(true);
        resume({})
          .catch((error: unknown) => notifyError(error, "Couldn't show the guide. Try again."))
          .finally(() => setIsResuming(false));
      }}
      disabled={isResuming}
    >
      <ListChecks />
      Setup guide
    </Button>
  );
}
